/*
 * DTFNY · Bulk variations picker
 * Custom element that:
 * - Reads variant JSON from inner <script type="application/json">
 * - Renders color swatches with search filter
 * - Manages size×qty matrix with live pricing/stock per selected color
 * - Submits bulk cart additions via /cart/add.js
 *
 * Markup contract: see snippets/dtfny-bulk-variations.liquid
 */
(function () {
  function formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '$0.00';
    return '$' + (cents / 100).toFixed(2);
  }

  // CSS colors considered "light" — used to flip the swatch checkmark to dark
  var LIGHT_COLORS = [
    'white', 'cream', 'ivory', 'eggshell', 'vanilla', 'pearl',
    'yellow', 'lemon', 'butter', 'gold', 'mustard',
    'beige', 'tan', 'sand', 'khaki', 'taupe',
    'silver', 'lightgray', 'lightgrey',
    'mint', 'lime', 'palegreen',
    'lightblue', 'lightpink', 'lightyellow',
    'pink', 'rose'
  ];

  function isLightColor(normalized) {
    if (!normalized) return false;
    return LIGHT_COLORS.some(function (k) { return normalized.indexOf(k) !== -1; });
  }

  // Preset packs (case-insensitive matching on size labels)
  var PRESETS = {
    small:    { 'S': 1, 'M': 2, 'L': 2, 'XL': 2, '2XL': 2, '3XL': 1, '4XL': 1, '5XL': 1 },
    standard: { 'S': 2, 'M': 4, 'L': 4, 'XL': 4, '2XL': 4, '3XL': 3, '4XL': 2, '5XL': 1 },
    large:    { 'S': 4, 'M': 6, 'L': 8, 'XL': 8, '2XL': 8, '3XL': 6, '4XL': 4, '5XL': 4 }
  };

  function BulkVariations() {
    return Reflect.construct(HTMLElement, [], BulkVariations);
  }
  BulkVariations.prototype = Object.create(HTMLElement.prototype);
  BulkVariations.prototype.constructor = BulkVariations;
  Object.setPrototypeOf(BulkVariations, HTMLElement);

  BulkVariations.prototype.connectedCallback = function () {
    if (this.dataset.dtfnyInit) return;
    this.dataset.dtfnyInit = '1';
    this.init();
  };

  BulkVariations.prototype.init = function () {
    var dataScript = this.querySelector('script[data-bulk-variants]');
    if (!dataScript) return;
    try {
      this.variants = JSON.parse(dataScript.textContent.trim());
    } catch (e) {
      console.error('[dtfny-bulk] Failed to parse variants:', e);
      return;
    }

    this.variantMap = new Map();
    var self = this;
    this.variants.forEach(function (v) {
      self.variantMap.set(v.color + '|' + v.size, v);
    });

    // Cache DOM
    this.swatches = Array.prototype.slice.call(this.querySelectorAll('button[data-color]'));
    this.dropdownItems = Array.prototype.slice.call(this.querySelectorAll('[data-dropdown-item]'));
    this.dropdownEmpty = this.querySelector('[data-dropdown-empty]');
    this.searchInput = this.querySelector('[data-color-search]');
    this.selectedSwatchEl = this.querySelector('[data-selected-swatch]');
    this.selectedNameEl = this.querySelector('[data-selected-name]');
    this.dropdownToggle = this.querySelector('[data-dropdown-toggle]');
    this.dropdownPanel = this.querySelector('[data-dropdown]');
    this.sizeCols = Array.prototype.slice.call(this.querySelectorAll('.dtfny-bulk__size-col[data-size]'));
    this.qtyInputs = Array.prototype.slice.call(this.querySelectorAll('input[data-qty]'));
    this.qtySteps = Array.prototype.slice.call(this.querySelectorAll('[data-qty-step]'));
    this.fillInput = this.querySelector('[data-fill-input]');
    this.fillBtn = this.querySelector('[data-fill-btn]');
    this.presets = Array.prototype.slice.call(this.querySelectorAll('[data-preset]'));
    this.submitBtn = this.querySelector('[data-submit]');
    this.submitText = this.querySelector('.dtfny-bulk__submit-text');
    this.submitSpinner = this.querySelector('.dtfny-bulk__submit-spinner');
    this.summaryQty = this.querySelector('[data-summary-qty]');
    this.summaryPrice = this.querySelector('[data-summary-price]');
    this.miniTotal = this.querySelector('[data-mini-total]');
    this.miniTotalQty = this.querySelector('[data-mini-qty]');
    this.miniTotalPrice = this.querySelector('[data-mini-price]');
    this.toast = this.querySelector('[data-toast]');
    this.toastText = this.querySelector('[data-toast-text]');
    this.submitTemplate = this.dataset.submitTemplate || 'Add {qty} items to cart';

    if (this.swatches.length === 0 || this.sizeCols.length === 0) return;

    // State
    this.state = {
      selectedColor: this.swatches[0].dataset.color,
      quantities: {},
      loading: false
    };
    this.sizeCols.forEach(function (col) {
      self.state.quantities[col.dataset.size] = 0;
    });

    // Optionally hijack the theme's Add to cart button (preferred UX)
    this.useThemeAtc = this.dataset.useThemeAtc === 'true';
    if (this.useThemeAtc) {
      this.hijackThemeAtc();
    }

    // Flag light-colored swatches so checkmark contrasts properly
    this.swatches.forEach(function (s) {
      if (isLightColor(s.dataset.colorNormalized)) s.setAttribute('data-light', 'true');
    });

    this.bindEvents();
    this.update();
  };

  BulkVariations.prototype.bindEvents = function () {
    var self = this;

    this.swatches.forEach(function (s) {
      s.addEventListener('click', function () {
        self.selectColor(s.dataset.color);
      });
    });

    this.qtyInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        self.onQtyInput(input.dataset.size, input.value);
      });
    });

    this.qtySteps.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var direction = parseInt(btn.dataset.qtyStep, 10) || 0;
        var col = btn.closest('.dtfny-bulk__size-col[data-size]');
        if (!col) return;
        var size = col.dataset.size;
        var current = self.state.quantities[size] || 0;
        var next = Math.max(0, current + direction);
        self.setQty(size, next);
      });
    });

    if (this.fillBtn) {
      this.fillBtn.addEventListener('click', function () {
        var val = parseInt(self.fillInput.value, 10);
        if (isNaN(val) || val < 0) val = 0;
        self.fillAll(val);
      });
    }
    if (this.fillInput) {
      this.fillInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (self.fillBtn) self.fillBtn.click();
        }
      });
    }

    this.presets.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.applyPreset(btn.dataset.preset);
      });
    });

    if (this.searchInput) {
      this.searchInput.addEventListener('input', function () {
        self.filterColors(self.searchInput.value);
      });
    }

    // Dropdown toggle
    if (this.dropdownToggle && this.dropdownPanel) {
      this.dropdownToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        self.toggleDropdown();
      });
      document.addEventListener('click', function (e) {
        if (!self.dropdownPanel.hasAttribute('hidden') && !self.contains(e.target)) {
          self.closeDropdown();
        }
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !self.dropdownPanel.hasAttribute('hidden')) {
          self.closeDropdown();
          self.dropdownToggle.focus();
        }
      });
    }

    // Dropdown item click → select color + close
    this.dropdownItems.forEach(function (item) {
      item.addEventListener('click', function () {
        self.selectColor(item.dataset.dropdownItem);
        self.closeDropdown();
      });
    });

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', function () { self.submit(); });
    }

    // Mobile fade hint — toggle data-scrolled-end when user scrolls to the end of size grid
    var sizeGrid = this.querySelector('[data-size-grid]');
    if (sizeGrid) {
      var updateScrollEnd = function () {
        var atEnd = sizeGrid.scrollLeft + sizeGrid.clientWidth >= sizeGrid.scrollWidth - 2;
        sizeGrid.setAttribute('data-scrolled-end', atEnd ? 'true' : 'false');
      };
      sizeGrid.addEventListener('scroll', updateScrollEnd, { passive: true });
      window.addEventListener('resize', updateScrollEnd);
      setTimeout(updateScrollEnd, 100);
    }
  };

  BulkVariations.prototype.toggleDropdown = function () {
    if (this.dropdownPanel.hasAttribute('hidden')) this.openDropdown();
    else this.closeDropdown();
  };

  BulkVariations.prototype.openDropdown = function () {
    this.dropdownPanel.removeAttribute('hidden');
    this.dropdownToggle.setAttribute('aria-expanded', 'true');
    var self = this;
    setTimeout(function () {
      if (self.searchInput) self.searchInput.focus();
    }, 50);
  };

  BulkVariations.prototype.closeDropdown = function () {
    this.dropdownPanel.setAttribute('hidden', '');
    this.dropdownToggle.setAttribute('aria-expanded', 'false');
    if (this.searchInput) {
      this.searchInput.value = '';
      this.filterColors('');
    }
  };

  BulkVariations.prototype.selectColor = function (color) {
    this.state.selectedColor = color;
    this.update();
    this.syncGalleryToColor();
  };

  // Find the variant for the selected color and click the matching thumbnail
  // in media-gallery so the main gallery image swaps to the color image.
  BulkVariations.prototype.syncGalleryToColor = function () {
    var sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    // Find first available variant for this color
    var sample = null;
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].color === this.state.selectedColor) {
        sample = this.variants[i];
        break;
      }
    }
    if (!sample || !sample.featured_media_id) return;

    var mediaGallery = document.getElementById('MediaGallery-' + sectionId);
    if (!mediaGallery) {
      mediaGallery = document.querySelector('media-gallery');
    }
    if (!mediaGallery) return;

    // Match thumbnail by data-target = "sectionId-mediaId"
    var thumb = mediaGallery.querySelector('[data-target$="-' + sample.featured_media_id + '"] .thumbnail');
    if (thumb && thumb.getAttribute('aria-current') !== 'true') {
      thumb.click();
    }
  };

  BulkVariations.prototype.setQty = function (size, qty) {
    this.state.quantities[size] = qty;
    var input = null;
    for (var i = 0; i < this.qtyInputs.length; i++) {
      if (this.qtyInputs[i].dataset.size === size) {
        input = this.qtyInputs[i];
        break;
      }
    }
    if (input) input.value = qty;
    this.updateColActive(size);
    this.updateTotals();
  };

  BulkVariations.prototype.onQtyInput = function (size, value) {
    var qty = parseInt(value, 10);
    if (isNaN(qty) || qty < 0) qty = 0;
    this.state.quantities[size] = qty;
    this.updateColActive(size);
    this.updateTotals();
  };

  BulkVariations.prototype.updateColActive = function (size) {
    var col = null;
    for (var i = 0; i < this.sizeCols.length; i++) {
      if (this.sizeCols[i].dataset.size === size) { col = this.sizeCols[i]; break; }
    }
    if (!col) return;
    var qty = this.state.quantities[size] || 0;
    col.classList.toggle('dtfny-bulk__size-col--active', qty > 0);
    var subtotalEl = col.querySelector('[data-subtotal]');
    if (!subtotalEl) return;
    var variant = this.variantMap.get(this.state.selectedColor + '|' + size);
    if (variant && qty > 0) {
      subtotalEl.textContent = formatMoney(variant.price * qty);
      subtotalEl.removeAttribute('data-empty');
    } else {
      subtotalEl.textContent = '—';
      subtotalEl.setAttribute('data-empty', 'true');
    }
  };

  BulkVariations.prototype.fillAll = function (qty) {
    var safeQty = Math.max(0, qty);
    var self = this;
    this.sizeCols.forEach(function (col) {
      var size = col.dataset.size;
      var variant = self.variantMap.get(self.state.selectedColor + '|' + size);
      if (!variant || !variant.available) return;
      self.setQty(size, safeQty);
    });
  };

  BulkVariations.prototype.applyPreset = function (preset) {
    var map = PRESETS[preset] || {};
    var self = this;
    this.sizeCols.forEach(function (col) {
      var sizeUpper = col.dataset.size.toUpperCase();
      var qty = map[sizeUpper] || 0;
      self.setQty(col.dataset.size, qty);
    });
  };

  BulkVariations.prototype.filterColors = function (query) {
    var q = (query || '').toLowerCase().trim();
    var matchCount = 0;
    this.dropdownItems.forEach(function (item) {
      var name = (item.dataset.dropdownItem || '').toLowerCase();
      var match = !q || name.indexOf(q) !== -1;
      var cell = item.parentElement;
      if (cell) cell.style.display = match ? '' : 'none';
      if (match) matchCount++;
    });
    if (this.dropdownEmpty) {
      if (matchCount === 0 && q) this.dropdownEmpty.removeAttribute('hidden');
      else this.dropdownEmpty.setAttribute('hidden', '');
    }
  };

  BulkVariations.prototype.update = function () {
    var self = this;
    // Swatch pressed state
    this.swatches.forEach(function (s) {
      s.setAttribute('aria-pressed', s.dataset.color === self.state.selectedColor ? 'true' : 'false');
    });

    // Selected color preview — try to use the swatch image if available, fallback to CSS color
    if (this.selectedNameEl) {
      this.selectedNameEl.textContent = this.state.selectedColor || '—';
    }
    if (this.selectedSwatchEl) {
      // Mirror the selected swatch's appearance (image OR solid color) onto the trigger.
      // We read data-swatch-image (set by Liquid when swatch_style=image and the variant has a featured_image),
      // and fall back to the swatch element's own computed background-color.
      var source = null;
      for (var s = 0; s < this.swatches.length; s++) {
        if (this.swatches[s].dataset.color === this.state.selectedColor) {
          source = this.swatches[s];
          break;
        }
      }
      if (!source) {
        for (var d = 0; d < this.dropdownItems.length; d++) {
          if (this.dropdownItems[d].dataset.dropdownItem === this.state.selectedColor) {
            source = this.dropdownItems[d].querySelector('.dtfny-bulk__dropdown-img') || this.dropdownItems[d];
            break;
          }
        }
      }
      var imgUrl = source && source.dataset ? source.dataset.swatchImage : '';
      if (imgUrl) {
        this.selectedSwatchEl.style.backgroundImage = 'url("' + imgUrl + '")';
        this.selectedSwatchEl.style.backgroundSize = 'cover';
        this.selectedSwatchEl.style.backgroundPosition = 'center';
        this.selectedSwatchEl.style.backgroundRepeat = 'no-repeat';
        this.selectedSwatchEl.style.backgroundColor = '';
      } else if (source) {
        var bg = window.getComputedStyle(source).backgroundColor;
        this.selectedSwatchEl.style.backgroundImage = '';
        this.selectedSwatchEl.style.backgroundColor = bg || '';
      } else {
        var normalized = (this.state.selectedColor || '').toLowerCase().replace(/\s/g, '');
        this.selectedSwatchEl.style.backgroundImage = '';
        this.selectedSwatchEl.style.backgroundColor = normalized;
      }
    }

    // Update each size column with current color's variant data
    this.sizeCols.forEach(function (col) {
      var size = col.dataset.size;
      var variant = self.variantMap.get(self.state.selectedColor + '|' + size);
      var priceEl = col.querySelector('[data-price]');
      var stockEl = col.querySelector('[data-stock]');
      var stockCount = col.querySelector('[data-stock-count]');
      var stockLabel = col.querySelector('[data-stock-label]');
      var qtyInput = col.querySelector('[data-qty]');

      col.classList.remove('dtfny-bulk__size-col--unavailable');

      if (!variant) {
        if (priceEl) priceEl.textContent = '—';
        if (stockEl) {
          stockEl.classList.remove('dtfny-bulk__stock--in', 'dtfny-bulk__stock--low', 'dtfny-bulk__stock--out');
          if (stockCount) stockCount.textContent = '—';
          if (stockLabel) stockLabel.textContent = 'Unavailable';
        }
        col.classList.add('dtfny-bulk__size-col--unavailable');
        if (qtyInput) qtyInput.disabled = true;
        return;
      }

      if (priceEl) priceEl.textContent = variant.price_formatted || formatMoney(variant.price);

      if (stockEl) {
        stockEl.classList.remove('dtfny-bulk__stock--in', 'dtfny-bulk__stock--low', 'dtfny-bulk__stock--out');
        var tracked = variant.inventory_management != null && variant.inventory_management !== '';
        var qty = variant.inventory_quantity;

        if (!variant.available) {
          stockEl.classList.add('dtfny-bulk__stock--out');
          if (stockCount) stockCount.textContent = '0';
          if (stockLabel) stockLabel.textContent = 'Sold out';
          col.classList.add('dtfny-bulk__size-col--unavailable');
        } else if (!tracked) {
          stockEl.classList.add('dtfny-bulk__stock--in');
          if (stockCount) stockCount.textContent = '✓';
          if (stockLabel) stockLabel.textContent = 'In stock';
        } else if (qty <= 10) {
          stockEl.classList.add('dtfny-bulk__stock--low');
          if (stockCount) stockCount.textContent = qty;
          if (stockLabel) stockLabel.textContent = 'Only left';
        } else {
          stockEl.classList.add('dtfny-bulk__stock--in');
          if (stockCount) stockCount.textContent = qty >= 1000 ? '999+' : String(qty);
          if (stockLabel) stockLabel.textContent = 'In stock';
        }
      }

      if (qtyInput) qtyInput.disabled = !variant.available;

      self.updateColActive(size);
    });

    this.updateTotals();
  };

  BulkVariations.prototype.updateTotals = function () {
    var totalQty = 0, totalCents = 0;
    var self = this;
    Object.keys(this.state.quantities).forEach(function (size) {
      var qty = self.state.quantities[size];
      if (qty <= 0) return;
      var variant = self.variantMap.get(self.state.selectedColor + '|' + size);
      if (variant && variant.available) {
        totalQty += qty;
        totalCents += variant.price * qty;
      }
    });

    if (this.summaryQty) this.summaryQty.textContent = totalQty === 1 ? '1 item' : totalQty + ' items';
    if (this.summaryPrice) this.summaryPrice.textContent = formatMoney(totalCents);

    if (this.submitBtn) {
      this.submitBtn.disabled = totalQty === 0 || this.state.loading;
      if (this.submitText) {
        this.submitText.textContent = totalQty === 0
          ? 'Select sizes'
          : this.submitTemplate.replace('{qty}', totalQty);
      }
    }

    // Mirror count + state on the theme's ATC button if we're hijacking it
    if (this.useThemeAtc && this.themeAtcBtn) {
      var label = totalQty === 0
        ? 'Select sizes'
        : this.submitTemplate.replace('{qty}', totalQty);
      this.themeAtcBtn.disabled = totalQty === 0 || this.state.loading;
      var inner = this.themeAtcBtn.querySelector('span:not(.svg-wrapper)');
      if (inner) inner.textContent = label;
      else this.themeAtcBtn.textContent = label;
    }

    // Compact total — only shown when hijacking theme ATC AND items selected
    if (this.miniTotal) {
      if (this.useThemeAtc && totalQty > 0) {
        this.miniTotal.removeAttribute('hidden');
        if (this.miniTotalQty) this.miniTotalQty.textContent = totalQty === 1 ? '1 item' : totalQty + ' items';
        if (this.miniTotalPrice) this.miniTotalPrice.textContent = formatMoney(totalCents);
      } else {
        this.miniTotal.setAttribute('hidden', '');
      }
    }
  };

  BulkVariations.prototype.submit = function () {
    if (this.state.loading) return;
    var items = [];
    var self = this;
    Object.keys(this.state.quantities).forEach(function (size) {
      var qty = self.state.quantities[size];
      if (qty <= 0) return;
      var variant = self.variantMap.get(self.state.selectedColor + '|' + size);
      if (variant && variant.available) {
        items.push({ id: variant.id, quantity: qty });
      }
    });
    if (items.length === 0) return;

    this.state.loading = true;
    this.submitBtn.disabled = true;
    if (this.submitText) this.submitText.hidden = true;
    if (this.submitSpinner) this.submitSpinner.hidden = false;

    var drawer = document.querySelector('cart-drawer');
    var sectionIds = (drawer && drawer.getSectionsToRender)
      ? drawer.getSectionsToRender().map(function (s) { return s.id; })
      : ['cart-drawer', 'cart-icon-bubble'];

    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        items: items,
        sections: sectionIds,                  // ask Shopify to render the drawer in the same response
        sections_url: window.location.pathname
      })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart add failed: ' + res.status);
        return res.json();
      })
      .then(function (state) {
        var totalItems = items.reduce(function (s, i) { return s + i.quantity; }, 0);
        self.sizeCols.forEach(function (col) { self.setQty(col.dataset.size, 0); });
        self.showToast('Added ' + totalItems + ' item' + (totalItems === 1 ? '' : 's') + ' to cart!', 'success');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        if (drawer && typeof drawer.renderContents === 'function' && state.sections) {
          drawer.renderContents(state);        // swaps in fresh cart HTML and opens
          drawer.classList.remove('is-empty'); // host keeps is-empty from an empty-cart load; clear it so items show on first add
        } else {
          self.refreshCart();                  // fallback when there is no drawer
        }
      })
      .catch(function (err) {
        console.error('[dtfny-bulk] Add to cart failed:', err);
        self.showToast('Sorry, something went wrong. Try again.', 'error');
      })
      .finally(function () {
        self.state.loading = false;
        self.submitBtn.disabled = false;
        if (self.submitText) self.submitText.hidden = false;
        if (self.submitSpinner) self.submitSpinner.hidden = true;
        self.updateTotals();
      });
  };

  BulkVariations.prototype.showToast = function (message, type) {
    if (!this.toast) return;
    if (this.toastText) this.toastText.textContent = message;
    this.toast.classList.toggle('dtfny-bulk__toast--error', type === 'error');
    this.toast.removeAttribute('hidden');
    var self = this;
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(function () {
      self.toast.setAttribute('hidden', '');
    }, 3500);
  };

  BulkVariations.prototype.hijackThemeAtc = function () {
    // Find Dawn's product form add-to-cart button in the same product-info container
    var scope = this.closest('product-info') || this.closest('section') || document;
    var btn = scope.querySelector('.product-form__submit, button[name="add"]');
    if (!btn) return;
    this.themeAtcBtn = btn;
    // Mark host element so CSS can hide the bulk picker's own summary
    this.classList.add('dtfny-bulk--use-theme-atc');
    // Intercept theme button click — submit bulk items instead of single variant
    var self = this;
    btn.addEventListener('click', function (e) {
      // Only intercept when we actually have items
      var totalQty = self.getTotalQty();
      if (totalQty > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.submit();
      }
    }, true);
  };

  BulkVariations.prototype.getTotalQty = function () {
    var total = 0;
    var self = this;
    Object.keys(this.state.quantities).forEach(function (size) {
      var qty = self.state.quantities[size];
      if (qty > 0) {
        var v = self.variantMap.get(self.state.selectedColor + '|' + size);
        if (v && v.available) total += qty;
      }
    });
    return total;
  };

  BulkVariations.prototype.refreshCart = function () {
    // Notify any cart UI
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Try to open Dawn's cart drawer
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    // Otherwise refresh the cart icon bubble in the header
    fetch('/?sections=cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var bubble = document.querySelector('cart-icon-bubble');
        if (bubble && data && data['cart-icon-bubble']) {
          var temp = document.createElement('div');
          temp.innerHTML = data['cart-icon-bubble'];
          var inner = temp.querySelector('cart-icon-bubble') || temp;
          bubble.innerHTML = inner.innerHTML;
        }
      })
      .catch(function () { /* silent fail */ });
  };

  if (!customElements.get('bulk-variations')) {
    customElements.define('bulk-variations', BulkVariations);
  }
})();
